import { useState, type ReactNode, type SubmitEvent } from 'react';
import { LockKeyhole } from 'lucide-react';
import { useI18n } from '../i18n/I18nContext';

const STORAGE_KEY = 'availora.access-gate';
const expectedPassword = import.meta.env.VITE_ACCESS_GATE_PASSWORD ?? '';
const accessVersion = import.meta.env.VITE_ACCESS_GATE_VERSION ?? '1';

/** Prüft, ob dieses Browserprofil bereits mit dem aktuell konfigurierten Passwort freigeschaltet wurde. */
function hasStoredAccess(): boolean {
  return Boolean(expectedPassword) && localStorage.getItem(STORAGE_KEY) === accessVersion;
}

/** Sperrt die SPA vor allen Authentifizierungs- und API-Aufrufen mit einem einfachen Vorschaltschutz. */
export function AccessGate({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const [isGranted, setIsGranted] = useState(hasStoredAccess);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  function handleSubmit(event: SubmitEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError('');

    if (!expectedPassword) {
      setError(t('error.accessNotConfigured'));
      return;
    }

    if (password !== expectedPassword) {
      setPassword('');
      setError(t('error.accessPasswordInvalid'));
      return;
    }

    localStorage.setItem(STORAGE_KEY, accessVersion);
    setIsGranted(true);
  }

  if (isGranted) return children;

  return (
    <main className="gate">
      <section className="gate-panel" aria-labelledby="access-gate-title">
        <div className="gate-mark" aria-hidden="true">
          <LockKeyhole size={24} />
        </div>
        <h1 id="access-gate-title">{t('access.title')}</h1>
        <form className="gate-form" onSubmit={handleSubmit}>
          <label htmlFor="access-password">{t('access.password')}</label>
          <input
            id="access-password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            autoFocus
            required
          />
          {error ? <p className="form-error" role="alert">{error}</p> : null}
          <button type="submit">{t('access.open')}</button>
        </form>
      </section>
    </main>
  );
}
